/**
 * AllTeamsItemsCard.js
 *
 * Displays all teams' purchased items in collapsible sections.
 * Each team can be expanded or collapsed to show their item list.
 * Budgets and prices are only shown if the host has made them public.
 */

import React, { useState } from "react";
import Card, { CardHeader, CardTitle, CardContent } from "../../../components/ui/Card";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getTeamItems } from "../library/libraries";
import { sortTeams } from "../assets/teamColors";
import { scoreTeam } from "../helpers/scoring";

const AllTeamsItemsCard = ({ allTeams, settings, library }) => {
    const itemsThatCount = settings.itemsToSubmit;
    const [openTeam, setOpenTeam] = useState(null);
    const teams = sortTeams(allTeams || []).filter((team) => team.members?.length > 0);
    const otherAttributes = library.attributes.filter((attribute) => attribute.kind !== "name" && attribute.kind !== "score");

    // If no data yet
    if (teams.length === 0) {
        return (
            <Card className="bg-zinc-900 border-zinc-700 shadow-md mt-6">
                <CardHeader>
                    <CardTitle className="text-white text-lg font-semibold">
                        All Teams’ Items
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="text-zinc-400 italic text-sm">
                        Nothing has been bought yet.
                    </p>
                </CardContent>
            </Card>
        );
    }

    /**
     * Toggles open/close for each team
     * @param {string} teamID
     */
    const toggleTeam = (teamID) => {
        setOpenTeam(openTeam === teamID ? null : teamID);
    };

    return (
        <Card className="bg-zinc-900 border-zinc-700 shadow-md mt-2">
            <CardHeader className="pb-2 text-left">
                <CardTitle className="text-sm text-zinc-400">All Teams' Items</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4 mt-2">
                {teams.map((team) => {
                    const isOpen = openTeam === team.id;
                    const items = getTeamItems(team);
                    // Score of the team's best submission so far, bonuses included
                    const countedScore = scoreTeam(team, settings, library).total;

                    return (
                        <div
                            key={team.id}
                            className="rounded-xl border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800/70 transition-all"
                        >
                            {/* Team Header */}
                            <button
                                onClick={() => toggleTeam(team.id)}
                                className="w-full flex justify-between items-center px-5 py-4 focus:outline-none"
                            >
                                <div className="flex items-center gap-3 flex-wrap">
                                    <h3 className="text-xl font-semibold text-zinc-100">
                                        {team.name}
                                    </h3>

                                    {settings.showBudgets && (
                                        <span className="text-sm text-zinc-400">₹{team.budgetLeft ?? 0} left</span>
                                    )}

                                    <span className="text-sm text-zinc-400">
                                        ({items.length} items)
                                    </span>

                                    {items.length > 0 && (
                                        <span className="text-sm text-yellow font-medium">
                                            • {items.length > itemsThatCount ? "Expected " : "Total "}{library.scoreAttribute}: {countedScore}
                                        </span>
                                    )}
                                </div>
                                {isOpen ? (
                                    <ChevronUp className="text-zinc-400 w-5 h-5" />
                                ) : (
                                    <ChevronDown className="text-zinc-400 w-5 h-5" />
                                )}
                            </button>

                            {/* Expandable Content */}
                            <div
                                className={`overflow-hidden transition-all duration-500 ${
                                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                                }`}
                            >
                                <div className="p-5 border-t border-zinc-700">
                                    {items.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {items.map((item, i) => (
                                                <div
                                                    key={i}
                                                    className="bg-zinc-900 p-4 rounded-xl border border-zinc-700 hover:border-zinc-500 transition-all text-left"
                                                >
                                                    <h4 className="text-base font-semibold text-white">
                                                        {item.name}
                                                    </h4>
                                                    {otherAttributes.map((attribute) => (
                                                        <p key={attribute.name} className="text-sm text-zinc-400">
                                                            {attribute.name}: {item.values?.[attribute.name] || "—"}
                                                        </p>
                                                    ))}
                                                    <p className="text-sm text-yellow font-medium mt-1">
                                                        {library.scoreAttribute}: {item.score}
                                                        {settings.showSales && item.soldPrice !== undefined && ` · ₹${item.soldPrice}`}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-zinc-500 italic text-sm">
                                            Nothing bought yet.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
};

export default AllTeamsItemsCard;
